import type { ReactNode } from 'react'
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix Leaflet default marker icons broken by the Vite asset pipeline (it
// rewrites the CSS-referenced default icon URLs) -- runs once at module
// load, not per-caller. Was duplicated identically in MetersPage.tsx and
// DispatcherView.tsx before this extraction.
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

// Mionica fallback center -- copy-pasted with the same comment across at
// least 5 call sites before this extraction (MetersPage, DispatcherView,
// ScadaView's TopologyView/pipeline editor/boundary editor/device-location
// editor).
export const FALLBACK_CENTER: [number, number] = [44.265, 20.085]

// Owns exactly the base map: the marker-icon fix (above), the OSM tile
// layer, and sizing. Stays with callers: marker icons/colors (domain-
// specific), popup content, click/drag handlers (DockedMapEditor's job,
// not the base map's).
// Named LeafletMap, not Map -- callers in this codebase (ScadaView.tsx
// especially) use the built-in JS Map constructor heavily for lookup
// tables, and a plain `Map` export would shadow it everywhere it's
// imported.
export function LeafletMap({ center, zoom = 14, heightClass = 'h-full', children }: {
  center: [number, number]
  zoom?: number
  heightClass?: string
  children?: ReactNode
}) {
  return (
    // zoomControl={false} + a bottomleft ZoomControl -- Leaflet's default
    // zoom control used to collide with DockedMapEditor's own top-left
    // "+ Dodaj tačku" button (caught by the user from a live screenshot);
    // that button has since moved into its parent modal's footer bar
    // instead (consolidating floating map-canvas buttons there), but
    // bottomleft is kept as the deliberate, collision-proof spot for any
    // future top-left overlay. Clear in every current caller: DockedMapEditor's
    // per-point panel docks bottom-*right*.
    <MapContainer center={center} zoom={zoom} className={`${heightClass} w-full`} scrollWheelZoom zoomControl={false}>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />
      <ZoomControl position="bottomleft" />
      {children}
    </MapContainer>
  )
}
